import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface City {
    id: number;
    name: string;
    place_count: number;
    description?: string;
    image?: string;
}

/* ── Skeleton ── */
const DestinationCardSkeleton: React.FC = () => (
    <div className="flex flex-col items-center gap-2 pb-2">
        <div className="h-[84px] w-[72px] flex-shrink-0 animate-pulse rounded-xl bg-gray-200 tablet:h-[112px] tablet:w-[92px]" />
        <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
    </div>
);

/* ── Card ── */
/* ── Card ── */
const DestinationCard: React.FC<{ city: City }> = ({ city }) => {
    const [loaded, setLoaded] = useState(false);

    const imageUrl = city.image
        ? city.image.startsWith("http")
            ? city.image
            : `${BASE_URL}${city.image}`
        : null;

    return (
        <Link
            to={`/places/city/${encodeURIComponent(city.name)}`}
            className="group flex flex-col items-center gap-2 pb-2"
        >
            <div className="relative h-[84px] w-[72px] flex-shrink-0 overflow-hidden rounded-xl bg-gray-100 tablet:h-[112px] tablet:w-[92px]">
                {imageUrl ? (
                    <>
                        {/* Blurred preview */}
                        <img
                            src={imageUrl}
                            alt=""
                            aria-hidden
                            className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl brightness-90"
                        />
                        {/* Full-res fades in */}
                        <img
                            src={imageUrl}
                            alt={city.name}
                            onLoad={() => setLoaded(true)}
                            className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-700 ${
                                loaded ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    </>
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300" />
                )}
            </div>
            <span className="text-xs md:text-sm font-myYekanDemibold text-gray-900 whitespace-nowrap text-center w-full block">
                {city.name}
            </span>
        </Link>
    );
};

/* ── Main component ── */
const PopularDestinations: React.FC = () => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    /* ── Fetch ── */
    const { data: cities = [], isLoading, isError } = useQuery<City[]>({
        queryKey: ["cities"],
        queryFn: async () => {
            const res = await fetch(`${BASE_URL}/api/cities/`);
            if (!res.ok) throw new Error("خطا در دریافت شهرها");
            const json = await res.json();
            return Array.isArray(json) ? json : json.data || [];
        },
        staleTime: 10 * 60 * 1000,
    });

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer) return;

        const updateScrollAvailability = () => {
            const containerBounds = scrollContainer.getBoundingClientRect();
            const cards = Array.from(scrollContainer.children) as HTMLElement[];

            setCanScrollPrev(
                cards.some((card) => card.getBoundingClientRect().right > containerBounds.right + 1)
            );
            setCanScrollNext(
                cards.some((card) => card.getBoundingClientRect().left < containerBounds.left - 1)
            );
        };

        const animationFrame = window.requestAnimationFrame(updateScrollAvailability);
        const resizeObserver = new ResizeObserver(updateScrollAvailability);

        resizeObserver.observe(scrollContainer);
        scrollContainer.addEventListener("scroll", updateScrollAvailability, { passive: true });
        window.addEventListener("resize", updateScrollAvailability);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            resizeObserver.disconnect();
            scrollContainer.removeEventListener("scroll", updateScrollAvailability);
            window.removeEventListener("resize", updateScrollAvailability);
        };
    }, [cities, isLoading, isError]);

    /* ── Scroll ── */
    const scroll = (dir: "prev" | "next") => {
        if (!scrollRef.current) return;
        const amount = scrollRef.current.clientWidth * 0.8;
        scrollRef.current.scrollBy({
            left: dir === "next" ? -amount : amount,
            behavior: "smooth",
        });
    };

    const getArrowClassName = () =>
        "flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-800 shadow-sm opacity-0 transition-opacity duration-200 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] pointer-events-none group-hover/destinations:pointer-events-auto group-hover/destinations:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100";

    /* ── Shared list ── */
    const renderItems = () => {
        if (isLoading)
            return Array.from({ length: 12 }).map((_, i) => (
                <DestinationCardSkeleton key={i} />
            ));
        if (isError)
            return (
                <p className="text-sm text-red-400 font-myYekanRegular px-4 py-6">
                    خطا در بارگذاری شهرها
                </p>
            );
return cities
    .filter((city) => !!city.image)
    .map((city) => <DestinationCard key={city.id} city={city} />);    };

    return (
        <div className="site-container pb-4 pt-1 tablet:py-4">
            {/* Header */}
            <div className="mb-1 flex items-center justify-between tablet:mb-4 tablet:px-12 desktop:px-0">
                <h2 className="text-lg tablet:text-xl desktop:text-2xl font-myYekanFaNumExtraBold">
                    مقاصد پرطرفدار
                </h2>
                {/* <Link
                    to="/destinations"
                    className="flex items-center gap-1 text-sm text-gray-800 font-myYekanFaNumMedium"
                >
                    مشاهده همه
                    <ChevronLeftIcon className="h-4 w-4" />
                </Link> */}
            </div>

            {/* Desktop */}
            <div className="group/destinations hidden tablet:block relative">
                {canScrollPrev && (
                    <button
                        onClick={() => scroll("prev")}
                        aria-label="مشاهده مقصدهای قبلی"
                        className={`${getArrowClassName()} absolute right-3 top-1/2 z-10 -translate-y-1/2`}
                    >
                        <ChevronRightIcon className="h-6 w-6" />
                    </button>
                )}

                <div
                    ref={scrollRef}
                    className="flex gap-6 overflow-x-auto pb-2 scrollbar-hide"
                    style={{ WebkitOverflowScrolling: "touch" }}
                >
                    {renderItems()}
                </div>

                {canScrollNext && (
                    <button
                        onClick={() => scroll("next")}
                        aria-label="مشاهده مقصدهای بعدی"
                        className={`${getArrowClassName()} absolute left-3 top-1/2 z-10 -translate-y-1/2`}
                    >
                        <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                )}
            </div>

            {/* Mobile */}
            <div className="tablet:hidden">
                <div
                    className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
                    style={{ WebkitOverflowScrolling: "touch" }}
                >
                    {renderItems()}
                </div>
            </div>

            <style>{`
                .scrollbar-hide::-webkit-scrollbar { display: none; }
                .scrollbar-hide { scrollbar-width: none; }
            `}</style>
        </div>
    );
};

export default PopularDestinations;
